import {
    Avatar,
    Box,
    Button,
    HStack,
    Icon,
    IconButton,
    Image,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack,
    Text,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    deleteComment,
    toggleBlockProfile,
    toggleLikeComment,
    toggleReportComment,
} from "../../api";
import { colors } from "../../styles";
import { IChatDetail, IComment } from "../../types";
import { formatRelativeTime } from "../../utils";
import MyIcon from "../Icon/MyIcon";
import ConfirmAlert, { useConfirmAlert } from "./ConfirmAlert";
import Reply from "./Reply";
import AnimatedReactionIcon from "./AnimatedReactionIcon";
import ReplyToModal from "../../routes/Chat/Detail/ReplyToModal";
import LikesModal from "../../routes/Chat/LikesModal";
import { ViewOffIcon } from "@chakra-ui/icons";
import { useChatDeleteLoading } from "./ChatDeleteLoading";

interface CommentProps {
    slug: string;
    comment: IComment;
    isFirst: boolean;
    isLast: boolean;
    onPostStart?: () => void;
    onPostDone?: () => void;
    onPostError?: () => void;
}

function Comment({
    slug,
    comment,
    isFirst,
    isLast,
    onPostStart,
    onPostDone,
    onPostError,
}: CommentProps) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const toast = useToast();
    const { showConfirm, confirmAlertProps } = useConfirmAlert();
    const deleteLoading = useChatDeleteLoading();
    const [likePulse, setLikePulse] = useState(0);
    const { isOpen: isLikesModalOpen, onOpen: onLikesModalOpen, onClose: onLikesModalClose } = useDisclosure();

    const {
        id,
        profile,
        content,
        likeCount,
        replyCount,
        replies,
        isMine,
        isLiked,
        isReported,
        deletedAt,
        hiddenAt,
        createdAt,
    } = comment;

    const handleGoToProfile = () => {
        navigate(`/chat/profile/${profile.nickname}`);
    };

    const toggleLikeCommentMutation = useMutation({
        mutationFn: () => toggleLikeComment({ slug, commentId: id }),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["chat", slug] });
            const previous = queryClient.getQueryData<IChatDetail>(["chat", slug]);

            queryClient.setQueryData<IChatDetail>(["chat", slug], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    comments: old.comments.map((c) =>
                        c.id === id
                            ? {
                                ...c,
                                isLiked: !c.isLiked,
                                likeCount: c.likeCount + (c.isLiked ? -1 : 1),
                            }
                            : c
                    ),
                };
            });

            return { previous };
        },
        onError: (error, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(["chat", slug], context.previous);
            }
            console.error(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
    });

    const toggleReportCommentMutation = useMutation({
        mutationFn: () => toggleReportComment({ slug, commentId: id }),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ["chat", slug] });
            const previous = queryClient.getQueryData<IChatDetail>(["chat", slug]);

            queryClient.setQueryData<IChatDetail>(["chat", slug], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    comments: old.comments.map((c) =>
                        c.id === id ? { ...c, isReported: !c.isReported } : c
                    ),
                };
            });

            return { previous };
        },
        onError: (error, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(["chat", slug], context.previous);
            }
            console.error(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
        },
    });

    const deleteCommentMutation = useMutation({
        mutationFn: () => deleteComment({ slug, commentId: id }),
        onSuccess: () => {
            confirmAlertProps.onClose();
            deleteLoading?.done();
        },
        onError: (error) => {
            console.error(error);
            deleteLoading?.error();
            toast({
                title: "댓글 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const toggleBlockProfileMutation = useMutation({
        mutationFn: () => toggleBlockProfile({ nickname: profile.nickname }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
            queryClient.invalidateQueries({ queryKey: ["chats"] });
        },
        onError: (error) => {
            console.error(error);
        },
    });

    const { isOpen: isReplyToCommentModalOpen, onOpen: onReplyToCommentModalOpen, onClose: onReplyToCommentModalClose } = useDisclosure();

    return (
        <Stack
            width={{
                base:"100%",
                sm:"100%",
                md:"100%",
                lg:"80%",
                xl:"80%",
            }}
            
            gap={3}

            border={`0.5px solid ${colors.g4}`}

            borderTopRadius={
                isFirst ? "30px" 
                    : "0"
            }
            borderBottomRadius={
                isLast ? "30px" 
                    : "0"
            }

            borderTop={isFirst ? `0.5px solid ${colors.g4}` : `none`}
            borderBottom={isLast ? `0.5px solid ${colors.g4}` : `none`}
            
            padding={5}

            boxShadow={"0px 1px 2px 0px rgba(0, 0, 0, 0.1)"}
        >
            <HStack width={"100%"} justifyContent={"space-between"} alignItems={"flex-start"}>
                <Box
                    onClick={handleGoToProfile}
                    cursor={"pointer"}
                    flex={1}
                    minWidth={0}
                >
                    <HStack gap={2} alignItems={"center"}>
                        {profile.avatar ?
                            <Image
                                src={profile.avatar}
                                alt={`${profile.nickname} 프로필 이미지`}
                                width={8}
                                height={8}
                                borderRadius={"full"}
                                objectFit={"cover"}
                                flexShrink={0}
                            />
                            :
                            <Image 
                                src={"/png/profile.png"}
                                alt="프로필 이미지"
                                width={8}
                                height={8}
                                borderRadius="full"
                                objectFit="cover"
                                flexShrink={0}
                            />
                        }
                        <HStack gap={2} alignItems={"center"}>
                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                {profile.nickname}
                            </Text>
                            <Text fontSize={12} fontWeight={500} color={colors.g3}>
                                {formatRelativeTime(createdAt)}
                            </Text>
                        </HStack>
                    </HStack>
                </Box>

                {isMine ?
                    (!deletedAt ? 
                        <Button
                            variant={"ghost"}
                            color={colors.g2}
                            flexShrink={0}
                            onClick={(e) => {
                                e.stopPropagation();
                                showConfirm({
                                    message: "이 댓글을 삭제하시겠습니까?",
                                    confirmLabel: "삭제",
                                    closeOnConfirm: false,
                                    onConfirm: () => {
                                        deleteLoading?.start();
                                        deleteCommentMutation.mutate();
                                    },
                                });
                            }}
                            disabled={deleteCommentMutation.isPending}
                        >
                            <MyIcon src={"/svg/delete-g1.svg"} alt={"삭제"} width={16} height={16} />
                        </Button>
                    : 
                        null
                    )
                    :
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            aria-label='Options'
                            icon={<MyIcon src={"/svg/more-g1.svg"} alt={"더보기"} width={16} height={16} />}
                            variant='ghost'
                        />
                        <MenuList>
                            <MenuItem
                                onClick={() =>
                                    showConfirm({
                                        message: isReported
                                            ? "신고를 취소하겠습니까?"
                                            : "이 댓글을 신고하겠습니까?",
                                        confirmLabel: isReported ? "신고 취소" : "신고",
                                        onConfirm: () => toggleReportCommentMutation.mutate(),
                                    })
                                }
                                disabled={toggleReportCommentMutation.isPending}
                            >
                                <Text color={colors.red}>
                                    {isReported ? "신고 취소" : "신고"}
                                </Text>
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    showConfirm({
                                        message: "이 사용자를 차단하겠습니까?",
                                        confirmLabel: "차단",
                                        onConfirm: () => toggleBlockProfileMutation.mutate(),
                                    })
                                }
                                disabled={toggleBlockProfileMutation.isPending}
                            >
                                사용자 차단
                            </MenuItem>
                        </MenuList>
                    </Menu>
                }
            </HStack>

            <Stack width={"100%"} paddingY={2}>
                {deletedAt ? (
                    <HStack width={"100%"} justifyContent={"flex-start"} alignItems={"center"}>
                        <Text fontSize={16} fontWeight={400} color={colors.g4} whiteSpace={"pre-wrap"}>
                            삭제된 댓글입니다.
                        </Text>
                    </HStack>
                ) : 
                hiddenAt ? (
                    <HStack width={"100%"} justifyContent={"flex-start"} alignItems={"center"}>
                        <Icon as={ViewOffIcon} color={colors.g4} width={5} height={5} />
                        <Text fontSize={16} fontWeight={400} color={colors.g4} whiteSpace={"pre-wrap"}>
                            관리자에 의해 가려진 댓글입니다.
                        </Text>
                    </HStack>
                ) : (
                    <Text fontSize={16} fontWeight={400} color={colors.black} whiteSpace={"pre-wrap"}>
                        {content}
                    </Text>
                )}
            </Stack>

            <HStack gap={5} alignItems={"center"}>
                <HStack
                    gap={1}
                    alignItems={"center"}
                    color={isLiked ? colors.red : colors.g2}
                >
                    <Box
                        as={"button"}
                        type={"button"}
                        display="flex"
                        alignItems="center"
                        onClick={() => {
                            setLikePulse((prev) => prev + 1);
                            toggleLikeCommentMutation.mutate();
                        }}
                        disabled={toggleLikeCommentMutation.isPending}
                    >
                        <AnimatedReactionIcon
                            src={isLiked ? "/svg/heart-filled.svg" : "/svg/heart.svg"}
                            alt={"좋아요"}
                            pulseKey={likePulse}
                        />
                    </Box>
                    <Text
                        as={"button"}
                        type={"button"}
                        fontSize={15}
                        fontWeight={500}
                        onClick={onLikesModalOpen}
                    >
                        {likeCount}
                    </Text>
                </HStack>
                <HStack gap={1} alignItems={"center"} color={colors.g2}>
                    <MyIcon src={"/svg/message-circle.svg"} alt={"답글"} width={20} height={20} />
                    <Text fontSize={15} fontWeight={500}>
                        {replyCount}
                    </Text>
                </HStack>
                <HStack 
                    as={"button"}
                    type={"button"}
                    gap={1} 
                    alignItems={"center"} 
                    color={colors.g2}
                    onClick={onReplyToCommentModalOpen}
                >
                    <Text fontSize={15} fontWeight={500}>
                        답글 쓰기
                    </Text>
                </HStack>

                {/* ReplyToModal */}
                
                <ReplyToModal
                    isOpen={isReplyToCommentModalOpen}
                    onClose={onReplyToCommentModalClose}
                    slug={slug}
                    comment={comment}
                    onPostStart={onPostStart}
                    onPostDone={onPostDone}
                    onPostError={onPostError}
                />
            </HStack>

            {replies.length > 0 && (
                <Stack 
                    width={"100%"} 
                    gap={0}
                    paddingY={5}
                    paddingLeft={{
                        base: 2.5,
                        sm: 2.5,
                        md: 2.5,
                        lg: 5,
                        xl: 5,
                    }}
                >
                    {replies.map((reply) => (
                        <Reply
                            key={reply.id}
                            slug={slug}
                            commentId={id}
                            reply={reply}
                        />
                    ))}
                </Stack>
            )}

            <LikesModal
                isOpen={isLikesModalOpen}
                onClose={onLikesModalClose}
                targetType="comment"
                targetId={id}
            />

            <ConfirmAlert
                {...confirmAlertProps}
                isLoading={
                    deleteCommentMutation.isPending ||
                    toggleReportCommentMutation.isPending ||
                    toggleBlockProfileMutation.isPending
                }
            />
        </Stack>
    );
}

export default React.memo(Comment);
